import { apiFetch } from './httpClient';

/** One row from GET /api/notifications. Mirrors the Notifications table projection. */
export type AppNotificationItem = {
    id: number;
    notificationType: string;
    resourceKey: string;
    recordKey: string;
    appRecordMessageId?: string | null;
    title: string;
    body?: string | null;
    isRead: boolean;
    readAtUtc?: string | null;
    createdAtUtc: string;
    createdByAppUserId?: string | null;
};

export type NotificationListResult = {
    data: AppNotificationItem[];
    total: number;
    unreadCount: number;
};

const EMPTY: NotificationListResult = { data: [], total: 0, unreadCount: 0 };

export async function fetchNotifications(
    options: { unreadOnly?: boolean; page?: number; perPage?: number } = {}
): Promise<NotificationListResult> {
    const q = new URLSearchParams({
        unreadOnly: String(options.unreadOnly ?? false),
        page: String(options.page ?? 1),
        perPage: String(options.perPage ?? 20),
    });
    const res = await apiFetch(`/api/notifications?${q.toString()}`, { method: 'GET' });
    if (!res.ok) throw new Error(`Notifications failed: ${res.status}`);
    const json = (await res.json()) as Partial<NotificationListResult>;
    return {
        data: Array.isArray(json.data) ? json.data : [],
        total: Number(json.total ?? 0),
        unreadCount: Number(json.unreadCount ?? 0),
    };
}

/**
 * The polling probe — one indexed COUNT server-side. Deliberately returns 0 instead of throwing:
 * a bell that silently keeps its last count through a blip is better than one that surfaces a
 * transient network error to a user who did not ask for anything.
 */
export async function fetchUnreadCount(): Promise<number> {
    try {
        const res = await apiFetch('/api/notifications/unread-count', { method: 'GET' });
        if (!res.ok) return 0;
        const json = (await res.json()) as { unreadCount?: unknown };
        return Number(json.unreadCount ?? 0);
    } catch {
        return 0;
    }
}

/** Marks one notification read. Idempotent; returns the caller's remaining unread count. */
export async function markNotificationRead(id: number): Promise<number> {
    const res = await apiFetch(`/api/notifications/${id}/read`, { method: 'POST' });
    if (!res.ok) throw new Error(`Mark read failed: ${res.status}`);
    const json = (await res.json()) as { unreadCount?: unknown };
    return Number(json.unreadCount ?? 0);
}

export async function markAllNotificationsRead(): Promise<void> {
    const res = await apiFetch('/api/notifications/read-all', { method: 'POST' });
    if (!res.ok) throw new Error(`Mark all read failed: ${res.status}`);
}

export const EMPTY_NOTIFICATION_LIST = EMPTY;
